import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import chatService, { ChatRoom, formatParticipantName } from '../services/chatService';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import { MessageCircle, Search, RefreshCw } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { getAccessToken } from '../services/authService';
import { Button } from './ui/button';
import { formatTimeAgo } from '../utils/timeUtils';

interface RoomsResponse {
  content?: ChatRoom[];
  [key: string]: any;
}

const ChatList: React.FC = () => {
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [filteredRooms, setFilteredRooms] = useState<ChatRoom[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false); // Start as false to show UI faster
  const [initialLoad, setInitialLoad] = useState(true); // Track if it's the first load
  const { refresh } = useAuth(); // Don't need isAuthenticated here
  const [userId] = useState(localStorage.getItem('userId'));
  const [error, setError] = useState<string | null>(null);
  
  // Process API response into chat rooms list
  const processRoomsResponse = useCallback((response: ChatRoom[] | { content: ChatRoom[] }): ChatRoom[] => {
    if (Array.isArray(response)) {
      console.log("ChatList: Processed array of rooms:", response.length);
      return response;
    } else if (response && typeof response === 'object') {
      const roomsResponse = response as RoomsResponse;
      if (roomsResponse.content && Array.isArray(roomsResponse.content)) {
        console.log("ChatList: Processed content property with rooms:", roomsResponse.content.length);
        return roomsResponse.content;
      }
    }
    return [];
  }, []);

  // Define loadChatRooms as a callback so it can be reused
  const loadChatRooms = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const token = getAccessToken();
      console.log("ChatList: Loading chat rooms with token:", token ? 'Token exists' : 'No token');
      
      if (!token) {
        console.log("ChatList: No token available, trying to refresh");
        try {
          await refresh(); // Try to refresh the token
          if (!getAccessToken()) {
            setError('No authentication token available');
            return false;
          }
        } catch (refreshError) {
          console.error("ChatList: Failed to refresh token:", refreshError);
          setError('Authentication error');
          return false;
        }
      }
      
      console.log("ChatList: Fetching chat rooms from server");
      const response = await chatService.getUserChatRooms();
      const roomsList = processRoomsResponse(response);
      
      if (roomsList.length > 0) {
        setChatRooms(roomsList);
        setFilteredRooms(roomsList);
        return true;
      } else {
        console.log("ChatList: No rooms found in response");
        setChatRooms([]);
        setFilteredRooms([]);
        return false;
      }
    } catch (error) {
      console.error('Failed to load chat rooms:', error);
      setError('Failed to load conversations');
      return false;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      setInitialLoad(false);
    }
  }, [refresh, processRoomsResponse]);

  // Load chat rooms once when component mounts
  useEffect(() => {
    loadChatRooms();
  }, [loadChatRooms]);

  // Periodically refresh chat rooms without showing loading state
  useEffect(() => {
    if (!getAccessToken()) return;
    
    console.log("ChatList: Setting up chat rooms refresh interval");
    
    const interval = setInterval(async () => {
      loadChatRooms(false); // Don't show loading indicator for refresh
    }, 30000);
    
    return () => clearInterval(interval);
  }, [loadChatRooms]);

  // Filter rooms when search query changes
  useEffect(() => {
    filterRooms(chatRooms, searchQuery);
  }, [searchQuery, chatRooms]);

  const filterRooms = (rooms: ChatRoom[], query: string) => {
    if (!query.trim()) {
      setFilteredRooms(rooms);
      return;
    }

    const lowercaseQuery = query.toLowerCase();
    const filtered = rooms.filter(room => {
      const otherParticipant = room.participants?.find(p => p.id !== userId);
      
      // Use formatParticipantName to get the display name for searching
      const displayName = otherParticipant ? formatParticipantName(otherParticipant).toLowerCase() : '';
      
      return (
        displayName.includes(lowercaseQuery) ||
        otherParticipant?.email?.toLowerCase().includes(lowercaseQuery) ||
        room.lastMessageContent?.toLowerCase().includes(lowercaseQuery)
      );
    });

    setFilteredRooms(filtered);
  };

  // Manual refresh handler
  const handleManualRefresh = () => {
    loadChatRooms(true);
  };

  // Different UI states
  const renderContent = () => {
    if (loading) {
      return (
        <div className="p-4 h-full flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-3"></div>
          <p className="text-gray-500">Loading conversations...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 h-full flex flex-col items-center justify-center">
          <div className="bg-red-100 p-5 rounded-full mb-3">
            <MessageCircle className="h-10 w-10 text-red-400" />
          </div>
          <p className="text-gray-600 font-medium">{error}</p>
          <p className="text-sm text-gray-400 text-center max-w-xs mt-1">
            Try refreshing the page
          </p>
          <Button 
            variant="outline" 
            onClick={handleManualRefresh} 
            className="mt-4"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      );
    }

    if (searchQuery.trim() && filteredRooms.length === 0) {
      return (
        <div className="p-4 h-full flex flex-col items-center justify-center">
          <div className="bg-gray-100 p-5 rounded-full mb-3">
            <Search className="h-10 w-10 text-gray-400" />
          </div>
          <p className="text-gray-500 font-medium">No matching conversations</p>
          <p className="text-sm text-gray-400 text-center max-w-xs mt-1">
            Try searching with a different term
          </p>
        </div>
      );
    }

    if (chatRooms.length === 0) {
      return (
        <div className="p-4 h-full flex flex-col items-center justify-center">
          <div className="bg-gray-100 p-5 rounded-full mb-3">
            <MessageCircle className="h-10 w-10 text-gray-400" />
          </div>
          <p className="text-gray-500 font-medium">No conversations yet</p>
          <p className="text-sm text-gray-400 text-center max-w-xs mt-1">
            When you chat with other users, your conversations will appear here
          </p>
          {initialLoad && (
            <Button 
              variant="outline" 
              onClick={handleManualRefresh} 
              className="mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>
      );
    }

    return (
      <ScrollArea className="flex-1">
        <div className="divide-y divide-gray-100">
          {filteredRooms.map(room => {
            const otherParticipant = room.participants?.find(p => p.id !== userId);
            const hasUnread = room.unreadCount > 0;
            
            return (
              <Link 
                key={room.id} 
                to={`/chat/${room.id}`}
                className={`block hover:bg-gray-50 transition duration-150 ${hasUnread ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center px-4 py-3 relative">
                  <div className="flex-shrink-0 relative">
                    <Avatar className={`h-12 w-12 ${hasUnread ? 'ring-2 ring-blue-300' : ''}`}>
                      <AvatarImage 
                        src={otherParticipant?.profilePicture} 
                        alt={otherParticipant ? formatParticipantName(otherParticipant) : "Unknown User"}
                      />
                      <AvatarFallback className={`${hasUnread ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-600'} font-semibold`}>
                        {otherParticipant?.fullName && !otherParticipant.fullName.includes("null") ? 
                          otherParticipant.fullName.charAt(0).toUpperCase() : 
                          otherParticipant?.email ? otherParticipant.email.charAt(0).toUpperCase() : '?'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium ${hasUnread ? 'text-blue-900' : 'text-gray-700'}`}>
                        {otherParticipant ? formatParticipantName(otherParticipant) : "Unknown User"}
                      </p>
                      {room.lastMessageAt && !isNaN(new Date(room.lastMessageAt).getTime()) && (
                        <p className={`text-xs ${hasUnread ? 'text-blue-800 font-medium' : 'text-gray-500'}`}>
                          {formatTimeAgo(room.lastMessageAt)}
                        </p>
                      )}
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <p className={`text-xs truncate w-56 ${hasUnread ? 'text-blue-800 font-medium' : 'text-gray-500'}`}>
                        {room.lastMessageContent || "No messages yet"}
                      </p>
                      {hasUnread && (
                        <span className="bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                          {room.unreadCount > 9 ? '9+' : room.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </ScrollArea>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-gray-50 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-semibold text-gray-800">Messages</h1>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleManualRefresh}
            disabled={loading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white border-gray-200 focus-visible:ring-blue-500"
          />
        </div>
      </div>
      
      {renderContent()}
    </div>
  );
};

export default ChatList;
